'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export function NeighborhoodHoverTooltip({
  open,
  neighborhoodName,
  anchor,
}: {
  open: boolean;
  neighborhoodName: string;
  anchor: { clientX: number; clientY: number } | null;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties | null>(null);

  // Position synchronously to reduce flicker.
  useLayoutEffect(() => {
    if (!open || !anchor) {
      setStyle(null);
      return;
    }

    // Initial guess: bottom-right of cursor.
    const offset = 12;
    setStyle({
      position: 'fixed',
      left: anchor.clientX + offset,
      top: anchor.clientY + offset,
      transform: 'translate(0, 0)',
    });
  }, [open, anchor]);

  // Refine position once we know tooltip dimensions.
  useEffect(() => {
    if (!open || !anchor || !tooltipRef.current) return;

    requestAnimationFrame(() => {
      if (!tooltipRef.current) return;
      const rect = tooltipRef.current.getBoundingClientRect();

      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const padding = 12;
      const offset = 12;

      // Preferred: bottom-right of cursor.
      let left = anchor.clientX + offset;
      let top = anchor.clientY + offset;

      // If overflowing right, move to left of cursor.
      if (left + rect.width > viewportW - padding) {
        left = anchor.clientX - offset - rect.width;
      }
      // If still overflowing, clamp to viewport.
      if (left < padding) left = padding;
      if (left + rect.width > viewportW - padding) left = viewportW - padding - rect.width;

      // If overflowing bottom, move above cursor.
      if (top + rect.height > viewportH - padding) {
        top = anchor.clientY - offset - rect.height;
      }
      if (top < padding) top = padding;
      if (top + rect.height > viewportH - padding) top = viewportH - padding - rect.height;

      setStyle({
        position: 'fixed',
        left,
        top,
        transform: 'translate(0, 0)',
      });
    });
  }, [open, anchor?.clientX, anchor?.clientY]);

  if (!open || !anchor || typeof document === 'undefined' || !style) return null;

  const tooltip = (
    <div
      ref={tooltipRef}
      className={cn(
        'pointer-events-none fixed z-50',
        'rounded-full bg-primary',
        'px-2 py-1',
        'shadow-[0px_4px_8px_-2px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.06)]',
      )}
      style={style}
      role="tooltip"
    >
      <div className="text-body-2 text-primary whitespace-nowrap">{neighborhoodName}</div>
    </div>
  );

  return createPortal(tooltip, document.body);
}
